/**
 * ローカルファースト進捗管理フック
 *
 * オフラインでもIndexedDBに進捗を保存し、
 * オンライン復帰時にサーバーと同期する。
 */
import { useCallback, useEffect, useRef, useState } from 'react'

import { getEpisodeProgress, updateEpisodeProgress } from '@/api/api'
import type { EpisodeProgress } from '@/api/type'
import { useOnlineStatus } from '@/hooks/useOnlineStatus'
import { getOfflineProgress, markProgressSynced, saveOfflineProgress } from '@/lib/db'
import { useProgressStore } from '@/store/useProgressStore'

type UseLocalProgressReturn = {
  /** 現在の進捗（ローカル or サーバー） */
  progress: EpisodeProgress | null
  isLoading: boolean
  /** 進捗を保存（ローカル + オンラインならサーバーも） */
  saveProgress: (value: number, cfi?: string | null) => Promise<void>
  /** 完了フラグを更新 */
  markCompleted: () => Promise<void>
}

export const useLocalProgress = (episodeId: number | null): UseLocalProgressReturn => {
  const { isOnline } = useOnlineStatus()
  const [progress, setProgress] = useState<EpisodeProgress | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const isOnlineRef = useRef(isOnline)

  // Ref を最新に保つ（コールバック内で使用）
  useEffect(() => {
    isOnlineRef.current = isOnline
  }, [isOnline])

  // 初期読み込み: ローカル → サーバー
  useEffect(() => {
    if (episodeId === null) {
      setIsLoading(false)
      return
    }

    let cancelled = false

    const load = async () => {
      setIsLoading(true)
      try {
        // まずローカルを確認
        const local = await getOfflineProgress(episodeId)
        if (cancelled) return

        if (local) {
          setProgress({
            episode_id: episodeId,
            progress: local.progress,
            cfi: local.cfi,
            locator: null,
            is_completed: local.isCompleted,
            play_count: 0,
            updated_at: new Date(local.updatedAt).toISOString(),
          })
        }

        // オンラインかつtokenが設定済みならサーバーからも取得し、新しい方を採用
        if (isOnlineRef.current && navigator.onLine) {
          try {
            const server = await getEpisodeProgress(episodeId)
            if (cancelled) return

            if (server) {
              const serverTime = new Date(server.updated_at).getTime()
              const localTime = local?.updatedAt ?? 0

              if (serverTime > localTime) {
                setProgress(server)
                // ローカルも更新
                await saveOfflineProgress({
                  episodeId,
                  progress: server.progress,
                  cfi: server.cfi ?? null,
                  isCompleted: server.is_completed,
                  updatedAt: serverTime,
                  synced: 1,
                })
              }
            }
          } catch {
            // オフライン/ネットワークエラー時は無視（ローカルデータを使用）
          }
        }
      } catch (error) {
        console.warn('進捗読み込みエラー:', error)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [episodeId])

  /** 進捗を保存 */
  const saveProgress = useCallback(
    async (value: number, cfi?: string | null) => {
      if (episodeId === null) return

      const now = Date.now()

      // ローカルに保存
      await saveOfflineProgress({
        episodeId,
        progress: value,
        cfi: cfi ?? null,
        isCompleted: false,
        updatedAt: now,
        synced: 0,
      })

      const updated: EpisodeProgress = {
        episode_id: episodeId,
        progress: value,
        cfi: cfi ?? null,
        locator: null,
        is_completed: false,
        play_count: 0,
        updated_at: new Date(now).toISOString(),
      }
      setProgress(updated)

      // 一覧画面（ProductContainer など）の表示を即座に合わせる
      useProgressStore.getState().applyEpisodeProgress(episodeId, value, false)

      // オンラインならサーバーにも送信
      if (isOnlineRef.current && navigator.onLine) {
        try {
          await updateEpisodeProgress(episodeId, {
            progress: value,
            cfi: cfi ?? undefined,
          })
          // synced フラグを立てる
          await markProgressSynced(episodeId)
        } catch {
          // オフラインに切り替わった可能性 → ローカルのみで十分
          console.warn('サーバー進捗保存に失敗（オフラインかも）')
        }
      }
    },
    [episodeId],
  )

  /** 完了フラグ */
  const markCompleted = useCallback(async () => {
    if (episodeId === null) return

    const now = Date.now()
    const currentProgress = progress?.progress ?? 100

    await saveOfflineProgress({
      episodeId,
      progress: currentProgress,
      cfi: progress?.cfi ?? null,
      isCompleted: true,
      updatedAt: now,
      synced: 0,
    })

    setProgress((prev) => (prev ? { ...prev, is_completed: true, updated_at: new Date(now).toISOString() } : null))

    useProgressStore.getState().applyEpisodeProgress(episodeId, currentProgress, true)

    if (isOnlineRef.current && navigator.onLine) {
      try {
        await updateEpisodeProgress(episodeId, {
          progress: currentProgress,
          is_completed: true,
        })
      } catch {
        console.warn('完了フラグ同期に失敗')
      }
    }
  }, [episodeId, progress])

  return { progress, isLoading, saveProgress, markCompleted }
}
